import { Component } from '@theme/component';
import { calculateMargin, parseAmount } from '@theme/margin-calculator-math';

/**
 * @typedef {Object} MarginRefs
 * @property {HTMLInputElement[]} fields
 * @property {HTMLSelectElement} vat
 * @property {HTMLElement[]} results
 * @property {HTMLScriptElement} strings
 * @property {HTMLElement} status
 * @property {HTMLDetailsElement} fees
 * @property {HTMLButtonElement[]} actions
 */

/** @extends {Component<MarginRefs>} */
class MarginCalculator extends Component {
  requiredRefs = ['fields', 'vat', 'results', 'strings', 'status', 'fees', 'actions'];
  #abort;
  #announcement;
  #downloadTimer;
  #downloadUrl;
  #strings;
  #money;
  #decimal;
  #inputNumber;
  #result;
  #values;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
    this.#abort?.abort();
    this.#abort = new AbortController();
    window.addEventListener('afterprint', () => this.removeAttribute('data-printing'), { signal: this.#abort.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#announcement);
    clearTimeout(this.#downloadTimer);
    if (this.#downloadUrl) URL.revokeObjectURL(this.#downloadUrl);
    this.#downloadUrl = undefined;
    this.removeAttribute('data-printing');
  }

  #initialize() {
    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');
    this.#money = new Intl.NumberFormat(this.lang || 'fr', { style: 'currency', currency: 'EUR' });
    this.#decimal = new Intl.NumberFormat(this.lang || 'fr', { maximumFractionDigits: 1 });
    this.#inputNumber = new Intl.NumberFormat(this.lang || 'fr', { maximumFractionDigits: 12 });
    this.dataset.ready = 'true';
    this.calculate();
  }

  /** Recompute from current refs after an input/change event or a section update. @param {Event} [event] */
  calculate(event) {
    const values = {};
    let hasError = false;
    for (const field of this.refs.fields) {
      const name = field.dataset.field;
      const value = parseAmount(field.value, field.required);
      let error = value === null ? this.#strings.errorNumber : '';
      if (value !== null && name === 'coefficient' && value <= 0) error = this.#strings.errorPositive;
      if (value !== null && name === 'quantity' && (!Number.isInteger(value) || value < 1)) error = this.#strings.errorQuantity;
      if (value !== null && (name === 'discount' || name === 'commission') && value > 100) error = this.#strings.errorPercent;
      if (value !== null && value > 100000000) error = this.#strings.errorLimit;
      const message = this.querySelector(`[data-error-for="${name}"]`);
      if (message instanceof HTMLElement) {
        message.textContent = error;
        message.hidden = !error;
      }
      field.setCustomValidity(error);
      if (error) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
      hasError ||= Boolean(error);
      values[name] = value;
    }
    values.vat = Number(this.refs.vat.value);
    this.#values = values;
    this.#result = hasError ? null : calculateMargin(values);
    for (const button of this.refs.actions) button.disabled = !this.#result;
    const result = this.#result;
    for (const element of this.refs.results) {
      const key = element.dataset.result;
      const value = result?.[key];
      const percent = ['margin_rate', 'markup_rate', 'contribution_rate', 'max_discount'].includes(key);
      element.textContent = value === null || value === undefined ? '—' : percent ? this.#decimal.format(value) + ' %' : this.#formatMoney(value);
    }
    for (const output of this.querySelectorAll('[data-print-input]')) {
      const name = output.getAttribute('data-print-input');
      const value = values[name];
      output.textContent = value === null || value === undefined ? '—' : ['purchase', 'shipping', 'packaging', 'fixed'].includes(name) ? this.#formatMoney(value) : this.#inputNumber.format(value) + (['vat', 'discount', 'commission'].includes(name) ? ' %' : name === 'coefficient' ? ' ×' : '');
    }
    let status = this.#strings.invalid;
    let state = 'invalid';
    if (result) {
      state = result.contribution < -0.005 ? 'negative' : result.contribution > 0.005 ? 'positive' : 'balanced';
      status = state === 'negative' ? this.#strings.negative : state === 'balanced' ? this.#strings.balanced : this.#strings.positive.replace('[amount]', this.#formatMoney(result.contribution));
      if (result.break_even_ttc === null) status += ' ' + this.#strings.noThreshold;
      const limit = this.querySelector('[data-limit-note]');
      if (limit) limit.textContent = result.max_discount === null ? this.#strings.noDiscount : this.#strings.discountNote;
    }
    this.dataset.state = state;
    clearTimeout(this.#announcement);
    if (event) this.#announcement = setTimeout(() => { this.refs.status.textContent = status; }, 300);
    else this.refs.status.textContent = status;
  }

  #formatMoney(value) {
    return this.#money.format(Math.abs(value) < 0.005 ? 0 : value);
  }

  /** Reset to the merchant's current saved defaults, including a blank fee allocation. */
  resetValues() {
    for (const field of this.refs.fields) field.value = field.defaultValue;
    this.refs.vat.value = [...this.refs.vat.options].find((option) => option.defaultSelected)?.value || '20';
    this.calculate();
  }

  /** Load an explicitly fictitious worked example; no catalogue prices are used. */
  loadExample() {
    const example = { purchase: 10, coefficient: 2.5, discount: 10, shipping: 12, quantity: 12, packaging: 0.5, commission: 2, fixed: 0.3 };
    for (const field of this.refs.fields) field.value = String(example[field.dataset.field]);
    this.refs.vat.value = '20';
    this.refs.fees.open = true;
    this.calculate();
  }

  /** Open the browser's print/PDF dialog with the full simulation summary. */
  printResult() {
    if (!this.#result) return;
    this.setAttribute('data-printing', '');
    try { window.print(); }
    catch { this.removeAttribute('data-printing'); }
  }

  /** Export labelled input and result rows with spreadsheet-safe, quoted CSV cells. */
  exportCsv() {
    if (!this.#result) return;
    const rows = [[this.#strings.csvLabel, this.#strings.csvValue]];
    for (const field of this.refs.fields) {
      const value = this.querySelector(`[data-print-input="${field.dataset.field}"]`);
      rows.push([field.dataset.fieldLabel, value?.textContent || field.value]);
    }
    rows.push([this.#strings.vatLabel, this.#decimal.format(this.#values.vat) + ' %']);
    for (const item of this.querySelectorAll('[data-export-row]')) {
      rows.push([item.querySelector('[data-export-label]')?.textContent?.trim() || '', item.querySelector('[data-result]')?.textContent || '']);
    }
    rows.push([this.#strings.csvScope, this.#strings.scope]);
    const escapeCell = (cell) => '"' + String(cell).replace(/^[=+@\t\r]/, "'").replaceAll('"', '""') + '"';
    const csv = '\uFEFF' + rows.map((row) => row.map(escapeCell).join(';')).join('\r\n');
    if (this.#downloadUrl) URL.revokeObjectURL(this.#downloadUrl);
    clearTimeout(this.#downloadTimer);
    this.#downloadUrl = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = this.#downloadUrl;
    link.download = 'simulation-marge-esotericae.csv';
    link.click();
    this.#downloadTimer = setTimeout(() => {
      if (this.#downloadUrl) URL.revokeObjectURL(this.#downloadUrl);
      this.#downloadUrl = undefined;
    }, 30000);
  }
}

if (!customElements.get('es-margin-calculator')) customElements.define('es-margin-calculator', MarginCalculator);
